/** @jsxImportSource @emotion/react */
'use client'

import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import type { LoginResponse } from '../../../../client'
import useAuth from '../../../hooks/useAuth'
import { loginErrorMessage } from '../../../utils/loginErrorMessage'
import { sanitizeInternalRedirect } from '../../../utils/safeRedirect'
import LoginForm from './LoginForm'
import OtpForm from './OtpForm'
import RecoverPasswordForm from './RecoverPasswordForm'
import ShowcaseCarousel from './ShowcaseCarousel'
import { styles } from './styles'
import useRememberedEmail from './useRememberedEmail'

interface LoginModalProps {
  open: boolean
  onClose: () => void
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose }) => {
  const { initialEmail, initialRemember, persist } = useRememberedEmail()

  const [username, setUsername] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(initialRemember)
  const [otpCode, setOtpCode] = useState('')
  const [loginHistoryId, setLoginHistoryId] = useState<number | null>(null)
  const [userId, setUserId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [view, setView] = useState<'login' | 'recover'>('login')
  const router = useRouter()

  const {
    loginMutation,
    handleOtpSubmit,
    otpError,
    setOtpError,
    requiresOtp,
    error,
    resetOtpRequirement,
    resetError,
  } = useAuth()

  const redirectAfterAuth = () => {
    // Check if there's a redirect URL stored from the login flow.
    const redirectUrl = sanitizeInternalRedirect(
      localStorage.getItem('loginRedirectUrl'),
      window.location.origin,
    )
    if (redirectUrl) {
      localStorage.removeItem('loginRedirectUrl') // Clear the stored URL

      // Use a slightly longer delay to ensure the modal is fully closed
      setTimeout(() => {
        router.push(redirectUrl)
      }, 300)
    }
  }

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      resetError('Please enter your email and password.')
      return
    }

    setIsLoading(true)
    try {
      resetError()
      const response: LoginResponse = await loginMutation.mutateAsync({
        username,
        password,
      })

      // Credentials were accepted (OTP is a second factor) — remember the email
      // only, never the password.
      persist(username, rememberMe)

      if (response.requiresOtp) {
        setLoginHistoryId(response.loginHistoryId ?? null)
        setUserId(response.userId ?? null)
      } else {
        onClose()
        redirectAfterAuth()
      }
    } catch (err: unknown) {
      resetError(loginErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmission = async () => {
    if (!otpCode.trim()) {
      setOtpError('Please enter your one-time passcode.')
      return
    }

    if (!loginHistoryId || !userId) {
      console.warn('Missing loginHistoryId or userId during OTP submission')
      setOtpError('Invalid login attempt. Please try logging in again.')
      return
    }

    setIsLoading(true)
    try {
      resetError()
      await handleOtpSubmit(userId, otpCode, loginHistoryId)
      if (!otpError) {
        onClose()
        redirectAfterAuth()
      }
    } catch (err) {
      console.error('OTP submission failed:', err)
      setOtpError('OTP submission failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Clean up any stored redirect URL when the modal is closed manually.
  const handleModalClose = () => {
    // Only clear the redirectUrl if we're not actively redirecting. This
    // prevents race conditions when manually closing after successful login.
    if (!isLoading) {
      localStorage.removeItem('loginRedirectUrl')
    }
    setView('login')
    onClose()
  }

  const handleForgotPassword = () => setView('recover')

  const handleOtpTimeout = useCallback(() => {
    setOtpCode('')
    setLoginHistoryId(null)
    setUserId(null)
    resetOtpRequirement()
    resetError('Your code expired. Please sign in again to get a new one.')
  }, [resetError, resetOtpRequirement])

  const ariaLabel = requiresOtp
    ? 'Enter your one-time passcode'
    : view === 'recover'
      ? 'Recover your password'
      : 'Sign in'

  return (
    <Modal open={open} onClose={handleModalClose}>
      <div
        css={styles.shell}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <IconButton
          aria-label="Close"
          onClick={handleModalClose}
          css={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>

        <div css={styles.formColumn}>
          <div css={styles.brand}>
            <Image
              src="/assets/images/WH-logo.png"
              alt="WyseHub logo"
              width={150}
              height={23}
              priority
              css={styles.logo}
            />
            <span css={styles.logoDivider} aria-hidden="true" />
            <Box
              component="img"
              src="/AiClaimsBlackLogo.svg"
              alt="AI Claims logo"
              width={150}
              height={25}
              css={styles.logoAiClaims}
            />
          </div>
          <div css={styles.formBody}>
            {requiresOtp ? (
              <OtpForm
                otpCode={otpCode}
                onOtpChange={setOtpCode}
                onSubmit={handleOtpSubmission}
                onBack={resetOtpRequirement}
                onTimeout={handleOtpTimeout}
                otpError={otpError}
                isLoading={isLoading}
              />
            ) : view === 'recover' ? (
              <RecoverPasswordForm
                initialEmail={username}
                onBack={() => setView('login')}
              />
            ) : (
              <LoginForm
                email={username}
                password={password}
                onEmailChange={setUsername}
                onPasswordChange={setPassword}
                rememberMe={rememberMe}
                onRememberMeChange={setRememberMe}
                onSubmit={handleLogin}
                onForgotPassword={handleForgotPassword}
                error={error}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>

        <div css={styles.showcaseColumn}>
          <ShowcaseCarousel />
        </div>
      </div>
    </Modal>
  )
}

export default LoginModal
